import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@apollo/client";

// GraphQL queries
import { getLinkedThirdPartyAccountsQuery } from "../components/EventModalV2/modules/EventModalV2Query";

// Selectors
import {
  getIsStaff,
  getUserEntityType,
  getUserId,
} from "modules/CommonSelectors";
import _ from "lodash";
import { checkUserAuthScopes, MEETING_SERVICE_OPTIONS } from "../modules/Utils";
import { isNewVideoMeetingUIFeatureEnabled } from "MyCalendar/components/EventModalV2/modules/Utils";

export const LinkedAccountContext = createContext();
const STABLE_EMPTY_ARRAY = [];
const STABLE_EMPTY_OBJECT = {};
/**
 * Provider component to manage and share linked third-party account data across components.
 * This provider fetches linked account information using GraphQL and provides it via context,
 * avoiding prop drilling and redundant API calls.
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Nested components that will consume the context
 * @returns {JSX.Element} The provider component
 */
const LinkedAccountProvider = ({ children }) => {
  const userId = getUserId();
  const userEntityType = getUserEntityType();

  const [
    localizedLinkedAccountsData,
    setLocalizedLinkedAccountsData,
  ] = useState([]);

  //q. why STABLE_EMPTY_OBJECT why not directly assign {}
  //a. when no data i.e for non staff member this will skip and return undefined
  // but default value will be empty object if it is ({}) then recreated on every render
  const {
    data: linkedAccountsData = STABLE_EMPTY_OBJECT,
    isLoading: isLinkedAccountsLoading,
    refetch: refetchLinkedAccounts,
  } = useQuery(getLinkedThirdPartyAccountsQuery, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      userId: userId,
      userType: userEntityType,
    },
    skip: !getIsStaff() || !isNewVideoMeetingUIFeatureEnabled(),
  });

  const value = useMemo(
    () => ({
      linkedThirdPartyAccountsList: localizedLinkedAccountsData,
      refetchLinkedAccounts,
      isLinkedAccountsLoading,
    }),
    [
      localizedLinkedAccountsData,
      isLinkedAccountsLoading,
      refetchLinkedAccounts,
    ]
  );

  useEffect(() => {
    const getAuthorizedLinkedAccountsData = async () => {
      const linkedThirdPartyAccountsList = _.get(
        linkedAccountsData,
        "node.linkedThirdPartyAccounts",
        STABLE_EMPTY_ARRAY
      );
      const localizedLinkedConnectedAccountsData = await Promise.all(
        _.map(linkedThirdPartyAccountsList, async accountType => {
          const isGoogleAccount =
            accountType.serviceType === MEETING_SERVICE_OPTIONS.GOOGLE;

          const accessToken = accountType?.accessToken;
          const hasAccessToken = !_.isEmpty(accessToken);
          let isGoogleAuthorized = false;

          // Check if Google account has the required scopes authorized
          // to create Google Meet links
          // Q. Why only Google?
          // A. Because currently we only need to check for Google Meet creation scopes for older linked accounts as
          // Microsoft and Zoom linked accounts always have the required scopes during linking process.
          if (hasAccessToken && isGoogleAccount) {
            isGoogleAuthorized = await checkUserAuthScopes({
              accessToken,
              scopes: [
                "https://www.googleapis.com/auth/meetings.space.created",
              ],
            });
            return {
              ...accountType,
              isConnected: isGoogleAuthorized,
            };
          }
          return await accountType;
        })
      );
      setLocalizedLinkedAccountsData(localizedLinkedConnectedAccountsData);
    };

    getAuthorizedLinkedAccountsData();
  }, [linkedAccountsData]);
  return !isNewVideoMeetingUIFeatureEnabled() ? (
    children
  ) : (
    <LinkedAccountContext.Provider value={value}>
      {children}
    </LinkedAccountContext.Provider>
  );
};

export default LinkedAccountProvider;

/**
 * Custom hook to access the LinkedAccount context.
 * Must be used within a LinkedAccountProvider.
 *
 * @returns {Object} The linked account context value containing:
 *   - linkedAccountsData: The fetched linked accounts data
 *   - isLinkedAccountsLoading: Loading state
 *   - refetchLinkedAccounts: Function to refetch the data
 * @throws {Error} If used outside of LinkedAccountProvider
 */
export const useLinkedAccountContext = () => {
  const linkedAccountContext = useContext(LinkedAccountContext);
  if (!linkedAccountContext) {
    throw new Error(
      "useLinkedAccountContext must be used within a LinkedAccountProvider"
    );
  }
  return linkedAccountContext;
};
